import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { Modal } from 'react-bootstrap';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import DeviceRequestForm from '../../components/DeviceRequests/DeviceRequestForm';
import DeviceRequestTimeline from '../../components/DeviceRequests/DeviceRequestTimeline';
import ImeiScanInput from '../../components/DeviceRequests/ImeiScanInput';
import { DR_API, STATUS, errMsg, fmtDateTime, patientLabel, toInputDate, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { isObjectId } from '../../utils/apiError';
import { Prod01 } from '../../constants/ActionType';

const OPEN = ['SENT_TO_EC', 'IN_PROGRESS', 'PARTIAL'];

/**
 * EC fulfilment: everything the nurse/OM entered (editable, audited), then one IMEI per requested device type.
 * Lookup uses the existing inventory lookup; the assignment + RPM start date/consent happen server-side via the existing services.
 */
const EcFulfil = () => {
    const { id } = useParams();
    const alert = useAlert();
    const [r, setR] = useState(null);
    const [error, setError] = useState('');
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [cannot, setCannot] = useState(false);
    const [reason, setReason] = useState('');
    const [formKey, setFormKey] = useState(0);

    const load = useCallback(async () => {
        if (!isObjectId(id)) { setError('This device request could not be found.'); return null; }
        try {
            const { data } = await axios.get(`${DR_API}/${id}`);
            setR(data);
            setFormKey((k) => k + 1);
            setStartDate((s) => s || toInputDate(data.programStartDate) || new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
            return data;
        } catch (err) { setError(errMsg(err)); return null; }
    }, [id]);
    useEffect(() => {
        load().then((data) => {
            if (data && data.status === 'SENT_TO_EC') axios.post(`${DR_API}/${id}/start`).then(load).catch(() => {});
        });
    }, [load, id]);

    const lookup = async (item, imei) => {
        setError('');
        setBusy(true);
        try {
            const { data } = await axios.get(`${Prod01}/inventory/devices/lookup`, { params: { code: imei, patientId: String(r.patientId) } });
            if (data.status === 'assigned_to_other') setError(`Device ${data.device?.imei} is already assigned to another patient. Unassign it there first.`);
            else if (data.status === 'assigned_to_this_patient') setError(`Device ${data.device?.imei} is already assigned to this patient.`);
            else if (data.status === 'wrong_org') setError(`Device ${data.device?.imei} belongs to a different organization.`);
            else setPending({ item, device: data.device });
        } catch (err) { setError(errMsg(err)); }
        finally { setBusy(false); }
    };

    const confirmAssign = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${DR_API}/${id}/fulfil`, { code: pending.device.imei, itemId: pending.item._id, programStartDate: startDate || null });
            alert.success(`${typeLabel(pending.item.deviceType)} ${pending.device.imei} assigned${data.status === 'FULFILLED' ? ' — request fulfilled' : ''}`);
            setPending(null);
            await load();
        } catch (err) { setError(errMsg(err)); setPending(null); }
        finally { setBusy(false); }
    };

    const markCannot = async () => {
        setBusy(true);
        try {
            await axios.post(`${DR_API}/${id}/cannot-fulfil`, { reason });
            alert.info('Sent back to the Org Manager');
            setCannot(false);
            await load();
        } catch (err) { setError(errMsg(err)); }
        finally { setBusy(false); }
    };

    if (!r) return <RoleShell title="Fulfil device request">{error ? <div className="alert alert-danger">{error}</div> : 'Loading…'}</RoleShell>;
    const open = OPEN.includes(r.status);
    const st = STATUS[r.status] || {};
    const pendingType = pending && pending.device ? String(pending.device.deviceType || '').toLowerCase() : '';

    return (
        <RoleShell title="Fulfil device request">
            <MetaData title="Fulfil device request" />
            <div className="d-flex justify-content-between">
                <h5>Fulfil {r.requestNo}: {patientLabel(r.patient)} <span className={`badge badge-${st.bg}`}>{st.label}</span></h5>
                <Link to="/enrollment/device-requests" className="btn btn-link btn-sm">Back to pending</Link>
            </div>
            <div className="small text-muted mb-2">{r.organization?.name} · batch {r.batch?.batchNo} · sent {fmtDateTime(r.sentAt)} · {r.patient?.emrId && `EHR-ID ${r.patient.emrId}`}</div>
            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
            <div className="row">
                <div className="col-lg-5 order-lg-2">
                    <div className="card mb-3"><div className="card-body">
                        <h6>Devices</h6>
                        <div className="mb-2">
                            <label className="form-label small" htmlFor="ec-start">RPM program start date</label>
                            <input id="ec-start" type="date" className="form-control form-control-sm" value={startDate} disabled={!open} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        {(r.items || []).map((item) => (
                            <div key={item._id} className="border rounded p-2 mb-2" data-testid={`dr-item-${item.deviceType}`}>
                                <strong>{typeLabel(item.deviceType)}</strong>{' '}
                                {item.status === 'FULFILLED' ? (
                                    <span className="text-success">✓ IMEI {item.imei} (device {item.deviceId}) · {item.fulfilledByName} {fmtDateTime(item.fulfilledAt)}</span>
                                ) : open ? (
                                    <ImeiScanInput label={`${typeLabel(item.deviceType)} IMEI`} disabled={busy || !!pending} autoFocus onImei={(imei) => lookup(item, imei)} />
                                ) : <span className="text-muted">pending</span>}
                            </div>
                        ))}
                        {open && !cannot && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setCannot(true)}>Cannot fulfil…</button>}
                        {cannot && (
                            <div className="mt-2">
                                <label className="form-label small" htmlFor="ec-reason">Reason (goes back to the Org Manager) *</label>
                                <textarea id="ec-reason" className="form-control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                                <button type="button" className="btn btn-danger btn-sm mt-1" disabled={busy || reason.trim().length < 3} onClick={markCannot}>Send back</button>
                                <button type="button" className="btn btn-link btn-sm mt-1" onClick={() => setCannot(false)}>Close</button>
                            </div>
                        )}
                    </div></div>
                    <div className="card"><div className="card-body"><h6>Timeline</h6><DeviceRequestTimeline history={r.history} /></div></div>
                </div>
                <div className="col-lg-7 order-lg-1">
                    <h6>Request details {open && <span className="small text-muted">(corrections are audited)</span>}</h6>
                    <DeviceRequestForm key={formKey} mode="edit" request={r} disabled={!open} submitLabel="Save corrections"
                        onSubmit={async (payload) => { await axios.patch(`${DR_API}/${id}`, payload); alert.success('Corrections saved'); await load(); }} />
                </div>
            </div>
            <Modal show={!!pending} onHide={() => setPending(null)}>
                <Modal.Header closeButton><Modal.Title>Assign device {pending?.device?.imei}</Modal.Title></Modal.Header>
                <Modal.Body>
                    Assign {pendingType || 'device'} {pending?.device?._id} (IMEI {pending?.device?.imei}) to {patientLabel(r.patient)} for the requested {pending && typeLabel(pending.item.deviceType)}?
                    RPM program start date {startDate || 'today'} and consent from this request will be saved.
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setPending(null)}>Cancel</button>
                    <button type="button" className="btn btn-primary" disabled={busy} onClick={confirmAssign}>Assign</button>
                </Modal.Footer>
            </Modal>
        </RoleShell>
    );
};

export default EcFulfil;
